import { useCachedFetch } from '@structural-climbing/auth'
import { useAuth } from '@structural-climbing/auth'

// Shape returned by GET /my-profile. A superset of CompletableProfile, so it can
// be handed straight to computeProfileCompletion.
export interface MyProfile {
  firstName: string | null
  lastName: string | null
  nickname: string | null
  avatarUrl: string | null
  isPublic: boolean
  language: string
  preferredGymId: string | null
  phone: string | null
  gender: string | null
  dateOfBirth: string | null
  // Absent from a Worker older than this app, which is why they are optional:
  // "not sent" must never be read as "not verified".
  /** The address on the account. */
  email?: string | null
  /** Whether the member has proved it by a code sent to it. */
  emailVerified?: boolean
  /** One of your ideas changed status: the trophy on Home gets its dot. */
  ideasUpdatePending?: boolean
}

// One cached /my-profile request shared by every caller — the dashboard header
// and the profile-completion badge both read it, and previously each fetched it
// on its own. refresh() re-pulls after the user edits their profile.
export function useMyProfile() {
  const { isAuthenticated, getAccessTokenSilently } = useAuth()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const { data, loading, refresh } = useCachedFetch<MyProfile>(
    isAuthenticated ? 'my-profile' : null,
    async () => {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/my-profile`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error('Failed to load profile')
      return (await res.json()) as MyProfile
    },
  )

  return { profile: data ?? null, loading, refresh }
}
